import type { components } from '@bookingdei/api-client/types';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { lrKindOf, type LrKind } from '../screens/listings/lr-cat';
import type { ScIconId } from './chrome-sprite';

type Category = components['schemas']['Category'];
type Lang = 'en' | 'bn';
type T = (key: MessageKey) => string;

export interface ScItem {
  label: string;
  sub: string;
  to: string;
  icon: ScIconId;
  badge?: string;
}

export interface ScCard {
  eyebrow: string;
  title: string;
  body: string;
  go: string;
  to: string;
}

export interface ScMenu {
  id: string;
  label: string;
  icon: ScIconId;
  /** The trigger is a link to the section's main page (Offers, Booking). */
  to?: string;
  groups: Array<{ title?: string; items: ScItem[] }>;
  card?: ScCard;
  /** A single short column (Resources) rather than the three-column mega panel. */
  one?: boolean;
  active: boolean;
}

// The design's three headline verticals and their icons (`i-bed / i-fork / i-ball`).
const KIND_ICON: Record<Exclude<LrKind, 'generic'>, ScIconId> = { hotel: 'sc-i-bed', restaurant: 'sc-i-fork', turf: 'sc-i-ball' };

// The mega menus' Categories column. The design lists Hotels & Resorts, Restaurants and
// Sports & Recreation; here those are the registry's categories whose booking template
// is hotel / restaurant / turf, in registry order, so labels and links follow the
// registry (FR-L6) and nothing is hardcoded. A tenant without those templates gets its
// first three categories with the generic icon. Sub-lines come from the registry's
// description when it has one, else the design's line for that kind.
export function headlineCategories(items: Category[] | undefined, lang: Lang, t: T): Array<{ id: string; label: string; sub: string; icon: ScIconId }> {
  const live = (items ?? []).filter((c) => c.enabled !== false && (c.status === undefined || c.status === 'live'));
  const kinded = live.filter((c) => lrKindOf(c) !== 'generic');
  const picked = (kinded.length ? kinded : live).slice(0, 3);
  return picked.map((c) => {
    const kind = lrKindOf(c);
    const desc = lang === 'bn' ? c.desc_bn : c.desc_en;
    return {
      id: c.id,
      label: (lang === 'bn' ? c.label_bn : c.label_en) || c.label_en,
      sub: desc?.trim() || t(`sc.cat_sub.${kind}` as MessageKey),
      icon: kind === 'generic' ? 'sc-i-grid' : KIND_ICON[kind],
    };
  });
}

export function buildChromeNav(pathname: string, categories: Category[] | undefined, lang: Lang, t: T) {
  const cats = headlineCategories(categories, lang, t);

  const offers: ScMenu = {
    id: 'sc-dd-offers',
    label: t('nav.offers'),
    icon: 'sc-i-pct',
    to: '/offer',
    active: /^\/offer(?:\/|$)/.test(pathname),
    groups: [
      {
        title: t('nav.categories'),
        items: cats.map((c) => ({ label: c.label, sub: c.sub, icon: c.icon, to: `/offer?category=${encodeURIComponent(c.id)}` })),
      },
      {
        title: t('nav.popular'),
        items: [
          { label: t('nav.popular_offers'), sub: t('sc.offers.live_sub'), icon: 'sc-i-flame', to: '/offer' },
          { label: t('nav.popular_trending'), sub: t('sc.offers.trending_sub'), icon: 'sc-i-spark', to: '/trending' },
          { label: t('nav.popular_offer_reels'), sub: t('sc.offers.reels_sub'), icon: 'sc-i-reel', to: '/videos#vd-offers', badge: t('nav.new_badge') },
        ],
      },
    ],
    card: {
      eyebrow: t('sc.offers.card_eyebrow'),
      title: t('sc.offers.card_title'),
      body: t('sc.offers.card_body'),
      go: t('sc.offers.card_go'),
      to: '/offer',
    },
  };

  // "Near you" is plain search: results already follow the visitor's location, and a
  // distance sort is deferred (DR-44).
  const booking: ScMenu = {
    id: 'sc-dd-booking',
    label: t('nav.booking'),
    icon: 'sc-i-cal',
    to: '/search',
    active: /^\/(?:search|business|b|l)(?:\/|$)/.test(pathname),
    groups: [
      {
        title: t('nav.categories'),
        items: cats.map((c) => ({ label: c.label, sub: c.sub, icon: c.icon, to: `/search?category=${encodeURIComponent(c.id)}` })),
      },
      {
        title: t('nav.popular'),
        items: [
          { label: t('nav.popular_trending'), sub: t('sc.book.trending_sub'), icon: 'sc-i-spark', to: '/search?sort=trending' },
          { label: t('nav.popular_near_you'), sub: t('sc.book.near_sub'), icon: 'sc-i-map', to: '/search' },
          { label: t('nav.popular_top_rated'), sub: t('sc.book.top_sub'), icon: 'sc-i-star', to: '/search?sort=rating' },
          { label: t('nav.popular_new_week'), sub: t('sc.book.new_sub'), icon: 'sc-i-bolt', to: '/search?sort=newest' },
        ],
      },
    ],
    card: {
      eyebrow: t('sc.book.card_eyebrow'),
      title: t('sc.book.card_title'),
      body: t('sc.book.card_body'),
      go: t('sc.book.card_go'),
      to: '/about',
    },
  };

  const resources: ScMenu = {
    id: 'sc-dd-resources',
    label: t('nav.resources'),
    icon: 'sc-i-grid',
    one: true,
    active: /^\/(?:for-business|blog|help)(?:\/|$)/.test(pathname),
    groups: [
      {
        items: [
          { label: t('nav.res_for_business'), sub: t('sc.res.business_sub'), icon: 'sc-i-brief', to: '/for-business' },
          { label: t('nav.res_blog'), sub: t('sc.res.blog_sub'), icon: 'sc-i-book', to: '/blog' },
          { label: t('nav.res_help'), sub: t('sc.res.help_sub'), icon: 'sc-i-life', to: '/help' },
        ],
      },
    ],
  };

  return { offers, booking, resources, videosActive: /^\/videos(?:\/|$)/.test(pathname) };
}

/** Whether a menu item's link is the page being shown (path only; query and hash ignored). */
export function isCurrent(to: string, pathname: string, search: string): boolean {
  if (to.includes('#')) return false;
  const [path, query = ''] = to.split('?');
  return path === pathname && (query ? `?${query}` : '') === search;
}
